import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { GanttWorkItem } from "../../types/gantt";
import { TimelineHeader } from "./TimelineHeader";
import { PlannedBar } from "./PlannedBar";
import { ActualBar } from "./ActualBar";
import { ForecastBar } from "./ForecastBar";
import { ZoomLevel } from "./ganttScale";
import { ROW_HEIGHT, WORK_COL_WIDTH } from "./ganttConstants";
import { addDays, subDays } from "date-fns";
import { ProjectAPI } from "../../api/apiClient";

/* ------------------------------------------------
   Timeline bounds
-------------------------------------------------- */
function getTimelineBounds(items: GanttWorkItem[]) {
  const dates: Date[] = [];
  const DAY_MS = 86400000;
  const PAD = 7;

  items.forEach(item => {
    if (item.planned) {
      dates.push(new Date(item.planned.start), new Date(item.planned.end));
    }
    if (item.forecast) {
      dates.push(new Date(item.forecast.start), new Date(item.forecast.end));
    }
    if (item.actual) {
      dates.push(new Date(item.actual.start), new Date(item.actual.end));
    }
  });

  const today = new Date();

  if (!dates.length) {
    return {
      start: new Date(today.getTime() - PAD * DAY_MS),
      end: new Date(today.getTime() + (7 + PAD) * DAY_MS),
    };
  }

  return {
    start: new Date(Math.min(...dates.map(d => d.getTime())) - PAD * DAY_MS),
    end: new Date(Math.max(...dates.map(d => d.getTime())) + PAD * DAY_MS),
  };
}

/* ------------------------------------------------
   Gantt Chart
-------------------------------------------------- */
function formatDisplayDate(value: string) {
  if (!value) return "";

  const d = new Date(value);

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  return `${day}/${month}/${year}`;
}

export function GanttChart({
  items,
  projectName,
  asOnDate,
  hideControls = false,
  printMode = false,
  initialExpandedIds = [],
  initialShowPlanned = true,
  initialShowForecast = true,
  initialShowActual = true,
}: {
  items: GanttWorkItem[];
  projectName: string;
  asOnDate: string;
  hideControls?: boolean;
  printMode?: boolean;
  initialExpandedIds?: number[];
  initialShowPlanned?: boolean;
  initialShowForecast?: boolean;
  initialShowActual?: boolean;
}) {
  const [zoom] = useState<ZoomLevel>("all");
  const [isExporting, setIsExporting] = useState(false);
  const [showPlanned, setShowPlanned] = useState(initialShowPlanned);
  const [showForecast, setShowForecast] = useState(initialShowForecast);
  const [showActual, setShowActual] = useState(initialShowActual);

  const [expanded, setExpanded] = useState<Set<number>>(
    () => new Set(initialExpandedIds)
  );

  const toggleNode = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  /* ---------------- CHILD MAP ---------------- */
  const childrenMap = new Map<number, GanttWorkItem[]>();
  const itemById = new Map<number, GanttWorkItem>(items.map(item => [item.workId, item]));

  items.forEach(item => {
    if (item.parentNode > 0 && item.parentNode !== item.workId) {
      if (!childrenMap.has(item.parentNode)) {
        childrenMap.set(item.parentNode, []);
      }

      childrenMap.get(item.parentNode)!.push(item);
    }
  });

  const expandAll = () => {
    const allParents = items
      .filter(item => childrenMap.has(item.workId))
      .map(item => item.workId);

    setExpanded(new Set(allParents));
  };

  const isParent = (item: GanttWorkItem) =>
    item.parentNode <= 0 || item.parentNode === item.workId;

  const rowBgClass = (item: GanttWorkItem) =>
    item.workId > 0 ? "bg-gray-100" : "bg-white";

  const getIndentLevel = (item: GanttWorkItem): number => {
    let indent = 0;
    let currentParent = item.parentNode;

    while (currentParent > 0 && currentParent !== item.workId) {
      const parentItem = itemById.get(currentParent);
      if (!parentItem) break;

      indent++;

      if (
        parentItem.parentNode <= 0 ||
        parentItem.parentNode === parentItem.workId
      ) {
        break;
      }

      currentParent = parentItem.parentNode;
    }

    return indent;
  };

  /* ---------------- ORDERED VISIBLE LIST ---------------- */
  const visibleItems: GanttWorkItem[] = [];

  function addNodeAndChildren(item: GanttWorkItem) {
    visibleItems.push(item);

    if (expanded.has(item.workId)) {
      const children = childrenMap.get(item.workId) || [];
      children.forEach(child => {
        addNodeAndChildren(child);
      });
    }
  }

  items.forEach(item => {
    const isRoot =
      item.parentNode <= 0 ||
      item.parentNode === item.workId ||
      !items.some(x => x.workId === item.parentNode);

    if (isRoot) {
      addNodeAndChildren(item);
    }
  });

  const isForecastOnly = showForecast && !showPlanned && !showActual;

  function hasForecastInTree(item: GanttWorkItem): boolean {
    if (item.forecast) return true;

    const children = childrenMap.get(item.workId) || [];
    return children.some(child => hasForecastInTree(child));
  }

  const leftColumnItems = isForecastOnly
    ? visibleItems.filter(item => hasForecastInTree(item))
    : visibleItems;

  /* ---------------- SCROLL / SIZE ---------------- */
  const leftBodyRef = useRef<HTMLDivElement>(null);
  const rightBodyRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const printPageRef = useRef<HTMLDivElement>(null);

  const [containerWidth, setContainerWidth] = useState(0);
  const [printTimelineWidth, setPrintTimelineWidth] = useState(0);

  useEffect(() => {
    if (printMode) return;
    if (!containerRef.current) return;

    const ro = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width);
    });

    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [printMode]);

  useLayoutEffect(() => {
    if (!printMode) return;

    const el = printPageRef.current;
    if (!el) return;

    const update = () => {
      const measured = Math.ceil(el.scrollWidth);
      setPrintTimelineWidth(prev => (prev === measured ? prev : measured));
    };

    update();

    const ro = new ResizeObserver(() => update());
    ro.observe(el);

    return () => ro.disconnect();
  }, [
    printMode,
    leftColumnItems.length,
    showPlanned,
    showForecast,
    showActual,
    expanded,
    projectName,
    asOnDate,
  ]);

  useEffect(() => {
    if (!printMode) return;

    (window as any).__ganttReady = false;

    const t = window.setTimeout(() => {
      (window as any).__ganttReady = true;
    }, 100);

    return () => window.clearTimeout(t);
  }, [
    printMode,
    items,
    expanded,
    showPlanned,
    showActual,
    showForecast,
    projectName,
    asOnDate,
  ]);

  const syncVertical = (e: React.UIEvent<HTMLDivElement>) => {
    const src = e.currentTarget;

    if (src === leftBodyRef.current && rightBodyRef.current) {
      rightBodyRef.current.scrollTop = src.scrollTop;
    }

    if (src === rightBodyRef.current && leftBodyRef.current) {
      leftBodyRef.current.scrollTop = src.scrollTop;
    }
  };

  const { start, end } = getTimelineBounds(visibleItems);

  const paddedStart = subDays(start, 15);
  const paddedEnd = addDays(end, 15);

  const totalDays = Math.max(
    1,
    (paddedEnd.getTime() - paddedStart.getTime()) / 86400000
  );

  const PRINT_WORK_WIDTH = 260;

  const workColumnWidth = printMode ? PRINT_WORK_WIDTH : WORK_COL_WIDTH;

  const timelineWidth = printMode
    ? (printTimelineWidth > 0 ? printTimelineWidth : 2200)
    : containerWidth > 0
      ? containerWidth
      : totalDays;

  const pxPerDay = timelineWidth / totalDays;

  const exportPdf = async () => {
    try {
      setIsExporting(true);

      const blob = await ProjectAPI.exportPdf(
        projectName,
        asOnDate,
        showPlanned,
        showActual,
        showForecast,
        Array.from(expanded),
        items
      );

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `Gantt_Report_${projectName}.pdf`;
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF Export Error:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const renderLeftCell = (item: GanttWorkItem) => {
    const indentLevel = getIndentLevel(item);

    return (
      <div
        className={`flex h-full items-center overflow-hidden w-full ${
          indentLevel > 0 ? "gap-2" : ""
        }`}
        style={{
          paddingLeft: `${indentLevel * 12}px`,
        }}
      >
        {childrenMap.has(item.workId) ? (
          <button
            onClick={() => toggleNode(item.workId)}
            className="w-5 shrink-0 text-sm font-bold leading-none"
            type="button"
          >
            {expanded.has(item.workId) ? "−" : "+"}
          </button>
        ) : (
          indentLevel > 0 && <span className="w-5 shrink-0" />
        )}

        <span
          className={`text-sm ${isParent(item) ? "font-semibold" : ""}`}
          style={{
            lineHeight: "1",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
          title={item.workElement}
        >
          {item.workElement}
        </span>
      </div>
    );
  };

  const rowCommonStyle: React.CSSProperties = {
    height: ROW_HEIGHT,
    minHeight: ROW_HEIGHT,
    maxHeight: ROW_HEIGHT,
    breakInside: "avoid",
    pageBreakInside: "avoid",
  };

  return (
    <div className="h-full min-h-0 flex flex-col bg-white">
      {!hideControls && !printMode && (
        <div className="flex items-center justify-end gap-3 px-4 py-3 border-b bg-gray-50 shadow-sm no-print">
          <button
            onClick={expandAll}
            className="px-4 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg shadow transition-all duration-200 hover:bg-emerald-700 hover:shadow-md active:scale-95"
            type="button"
          >
            Expand All
          </button>

          <button
            onClick={() => setExpanded(new Set())}
            className="px-4 py-2 text-sm font-medium bg-amber-500 text-white rounded-lg shadow transition-all duration-200 hover:bg-amber-600 hover:shadow-md active:scale-95"
            type="button"
          >
            Collapse All
          </button>

          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={showPlanned}
              onChange={e => setShowPlanned(e.target.checked)}
            />
            Planned
          </label>

          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={showActual}
              onChange={e => setShowActual(e.target.checked)}
            />
            Actual
          </label>

          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={showForecast}
              onChange={e => setShowForecast(e.target.checked)}
            />
            Forecast
          </label>

         {/*  <button
            onClick={exportPdf}
            disabled={isExporting}
            className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg shadow transition-all duration-200 hover:bg-blue-700 hover:shadow-md active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            type="button"
          >
            {isExporting ? "Exporting..." : "Export PDF"}
          </button> */}
        </div>
      )}

      <div
        className={`print-area flex flex-col min-h-0 flex-1 pb-16 ${
          printMode ? "w-full overflow-visible" : ""
        }`}
      >
        <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50">
          <div className="flex justify-center w-full">
            <div className="flex items-center text-base font-semibold text-gray-900">
              <span className="font-bold">Gantt Chart - {projectName}</span>
              <span className="ml-3 font-bold">
                As on Date: {formatDisplayDate(asOnDate)}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-6 text-sm">
            {showPlanned && (
              <div className="flex items-center gap-2">
                <span className="w-4 h-3 bg-blue-600 rounded-sm" />
                <span>Planned</span>
              </div>
            )}

            {showActual && (
              <div className="flex items-center gap-2">
                <span className="w-4 h-3 bg-orange-500 rounded-sm" />
                <span>Actual</span>
              </div>
            )}

            {showForecast && (
              <div className="flex items-center gap-2">
                <span className="w-4 h-3 border border-gray-700 border-dashed bg-gray-400 rounded-sm" />
                <span>Forecast</span>
              </div>
            )}
          </div>
        </div>

        {/* PRINT MODE: one row = one label + one bar area */}
        {printMode ? (
          <div
            ref={printPageRef}
            className="overflow-visible"
            style={{
              width: "max-content",
              minWidth: "100%",
              height: "auto",
            }}
          >
            <div className="flex border-b bg-gray-50" style={{ height: ROW_HEIGHT }}>
              <div
                style={{ width: workColumnWidth }}
                className="border-r px-3 font-semibold flex items-center"
              >
                Work
              </div>

              <div style={{ width: timelineWidth }} className="pdf-header">
                <TimelineHeader
                  timelineStart={paddedStart}
                  timelineEnd={paddedEnd}
                  pxPerDay={pxPerDay}
                  zoom={zoom}
                />
              </div>
            </div>

            <div className="w-full overflow-visible">
              {leftColumnItems.map(item => (
                <div
                  key={item.workId}
                  className={`gantt-row flex border-b ${rowBgClass(item)}`}
                  style={rowCommonStyle}
                >
                  <div
                    style={{ width: workColumnWidth }}
                    className="border-r px-3 flex items-center overflow-hidden"
                  >
                    {renderLeftCell(item)}
                  </div>

                  <div
                    className="relative overflow-visible"
                    style={{ width: timelineWidth }}
                  >
                    {showPlanned && item.planned && (
                      <PlannedBar
                        item={item}
                        timelineStart={paddedStart}
                        pxPerDay={pxPerDay}
                        timelineWidth={timelineWidth}
                      />
                    )}

                    {showForecast && item.forecast && (
                      <ForecastBar
                        item={item}
                        timelineStart={paddedStart}
                        pxPerDay={pxPerDay}
                        timelineWidth={timelineWidth}
                      />
                    )}

                    {showActual && item.actual && (
                      <ActualBar
                        item={item}
                        timelineStart={paddedStart}
                        pxPerDay={pxPerDay}
                        timelineWidth={timelineWidth}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* NORMAL SCREEN MODE */
          <div className="border rounded flex flex-1 min-h-0 overflow-hidden">
            {/* LEFT COLUMN */}
            <div className="shrink-0 bg-white z-10 flex flex-col min-h-0">
              <div
                className="flex border-b bg-gray-50"
                style={{ height: ROW_HEIGHT }}
              >
                <div
                  style={{ width: workColumnWidth }}
                  className="border-r px-3 font-semibold"
                >
                  Work
                </div>
              </div>

              <div
                ref={leftBodyRef}
                onScroll={syncVertical}
                className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide"
              >
                {leftColumnItems.map(item => (
                  <div
                    key={item.workId}
                    className={`border-b flex items-center ${rowBgClass(item)}`}
                    style={{
                      width: workColumnWidth,
                      height: ROW_HEIGHT,
                      minHeight: ROW_HEIGHT,
                      maxHeight: ROW_HEIGHT,
                    }}
                  >
                    <div
                      className={`flex h-full items-center overflow-hidden w-full ${
                        getIndentLevel(item) > 0 ? "gap-2" : ""
                      }`}
                      style={{
                        paddingLeft: `${getIndentLevel(item) * 12}px`,
                      }}
                    >
                      {childrenMap.has(item.workId) ? (
                        <button
                          onClick={() => toggleNode(item.workId)}
                          className="w-5 shrink-0 text-sm font-bold leading-none"
                          type="button"
                        >
                          {expanded.has(item.workId) ? "−" : "+"}
                        </button>
                      ) : (
                        getIndentLevel(item) > 0 && <span className="w-5 shrink-0" />
                      )}

                      <span
                        className={`text-sm ${isParent(item) ? "font-semibold" : ""}`}
                        style={{
                          lineHeight: "1",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "flex",
                          alignItems: "center",
                          height: "100%",
                        }}
                        title={item.workElement}
                      >
                        {item.workElement}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="flex-1 flex flex-col min-h-0">
              <div
                className="pdf-header gantt-header bg-white border-b"
                style={{ width: timelineWidth }}
              >
                <TimelineHeader
                  timelineStart={paddedStart}
                  timelineEnd={paddedEnd}
                  pxPerDay={pxPerDay}
                  zoom={zoom}
                />
              </div>

              <div
                ref={el => {
                  rightBodyRef.current = el;
                  containerRef.current = el;
                }}
                onScroll={syncVertical}
                className="flex-1 overflow-y-auto overflow-x-hidden"
              >
                <div className="pdf-page relative" style={{ width: timelineWidth, minWidth: timelineWidth }}>
                  {leftColumnItems.map(item => (
                    <div
                      key={item.workId}
                      className={`gantt-row relative border-b break-inside-avoid ${rowBgClass(item)}`}
                      style={{
                        height: ROW_HEIGHT,
                        minHeight: ROW_HEIGHT,
                        maxHeight: ROW_HEIGHT,
                        width: timelineWidth,
                      }}
                    >
                      {showPlanned && item.planned && (
                        <PlannedBar
                          item={item}
                          timelineStart={paddedStart}
                          pxPerDay={pxPerDay}
                          timelineWidth={timelineWidth}
                        />
                      )}

                      {showForecast && item.forecast && (
                        <ForecastBar
                          item={item}
                          timelineStart={paddedStart}
                          pxPerDay={pxPerDay}
                          timelineWidth={timelineWidth}
                        />
                      )}

                      {showActual && item.actual && (
                        <ActualBar
                          item={item}
                          timelineStart={paddedStart}
                          pxPerDay={pxPerDay}
                          timelineWidth={timelineWidth}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
